import { useState } from "react";
import { Github, Check, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { StatusDot } from "@/components/ui/status-dot";
import { api } from "@/lib/api";

interface Installation {
  id: string;
  installation_id: number;
  account_login: string;
  account_type: string;
  suspended_at: string | null;
}

interface GitHubStatusData {
  installations: Installation[];
  just_connected?: boolean;
}

interface GitHubStatusWidgetProps {
  data: GitHubStatusData;
  actions?: Array<{ id: string; label: string; style?: string }>;
  onAction?: (actionId: string) => void;
}

export function GitHubStatusWidget({ data, actions }: GitHubStatusWidgetProps) {
  const [installations, setInstallations] = useState(data.installations ?? []);
  const [disconnecting, setDisconnecting] = useState<string | null>(null);
  const [disconnected, setDisconnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const showDisconnect = actions?.some((a) => a.id === "disconnect");

  async function handleDisconnect(id: string) {
    setDisconnecting(id);
    setError(null);
    try {
      await api.github.disconnect(id);
      const remaining = installations.filter((i) => i.id !== id);
      setInstallations(remaining);
      if (remaining.length === 0) {
        setDisconnected(true);
      }
    } catch {
      // UUID may be stale (installation deleted and recreated) — refresh from server
      try {
        const fresh = await api.github.installations();
        if (fresh.length === 0) {
          // Already disconnected on the server side
          setInstallations([]);
          setDisconnected(true);
          return;
        }
        // Update with fresh data so next click uses correct UUIDs
        setInstallations(fresh as Installation[]);
        setError("Installation changed. Please try again.");
      } catch {
        setError("Failed to disconnect. Please try again.");
      }
    } finally {
      setDisconnecting(null);
    }
  }

  if (disconnected) {
    return (
      <Card variant="default" padding="sm" className="max-w-md animate-slide-up">
        <div className="flex items-center gap-2">
          <Github size={14} className="text-daydra-muted" />
          <p className="text-sm text-daydra-dim">GitHub disconnected</p>
        </div>
      </Card>
    );
  }

  return (
    <Card variant="default" padding="none" className="overflow-hidden max-w-md animate-slide-up">
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-daydra-accent-ghost">
          <Github size={16} className="text-daydra-accent" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-daydra-text">
            {data.just_connected ? "GitHub connected" : "GitHub"}
          </p>
          {data.just_connected && (
            <div className="flex items-center gap-1.5 mt-0.5">
              <StatusDot status="connected" size="sm" label="Connected" labelHidden={false} />
            </div>
          )}
        </div>
        {data.just_connected && (
          <Check size={18} className="text-daydra-success shrink-0" />
        )}
      </div>

      {installations.length > 0 && (
        <div className="border-t border-daydra-border">
          {installations.map((inst) => (
            <div
              key={inst.id}
              className="flex items-center gap-2 px-4 py-2.5"
            >
              <Github size={14} className="text-daydra-muted shrink-0" />
              <span className="flex-1 text-sm text-daydra-text truncate">
                {inst.account_login}
              </span>
              <Badge variant="default" size="sm">
                {inst.account_type}
              </Badge>
              {showDisconnect && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleDisconnect(inst.id)}
                  disabled={disconnecting === inst.id}
                >
                  {disconnecting === inst.id ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    "Disconnect"
                  )}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="px-4 py-2 border-t border-daydra-border">
          <p className="text-xs text-daydra-error">{error}</p>
        </div>
      )}
    </Card>
  );
}
